/**
 * RomaniaCountyMap — Production-grade geographic heatmap.
 *
 * Renders real Romania SVG county geometry (from Romania_judete.svg).
 * Each county is an individual <motion.path> with dynamic fill, hover state,
 * tooltip, click selection and animation.
 *
 * Future-ready: any CountyMetrics shape can drive `metric` (delivery rate,
 * avg days, return rate, COD collected, profit, courier comparison, ...).
 */
import { useMemo, useState, useRef, useCallback } from "react";
import { motion } from "framer-motion";
import { ROMANIA_COUNTIES, COUNTY_NAMES_BY_CODE } from "./_counties_geo";

/**
 * Normalize a province / county name to its ISO code (`RO-XX`-style without
 * the `RO-` prefix). Handles diacritics, spaces, common Shopify variants.
 */
const NAME_TO_CODE = (() => {
  const map = {};
  for (const c of ROMANIA_COUNTIES) {
    const norm = c.name.toLowerCase()
      .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
      .replace(/[șş]/g, "s").replace(/[țţ]/g, "t")
      .replace(/[ăâ]/g, "a").replace(/î/g, "i").trim();
    map[norm] = c.code;
  }
  // Aliases
  map["bucharest"] = "B";
  map["bucuresti"] = "B";
  map["municipiul bucuresti"] = "B";
  return map;
})();

export function normalizeCountyName(name) {
  if (!name) return null;
  const n = String(name).toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[șş]/g, "s").replace(/[țţ]/g, "t")
    .replace(/[ăâ]/g, "a").replace(/î/g, "i").trim();
  return NAME_TO_CODE[n] || null;
}

// Color schemes per metric (lower = worse for "delivery_rate"; higher = worse for rates)
const METRIC_CONFIG = {
  orders:       { label: "Total colete",         polarity: "more_is_better", unit: "" },
  delivered:    { label: "Livrate",              polarity: "more_is_better", unit: "" },
  refused:      { label: "Refuzate",             polarity: "less_is_better", unit: "" },
  returned:     { label: "Retur expeditor",      polarity: "less_is_better", unit: "" },
  in_transit:   { label: "În tranzit",           polarity: "more_is_better", unit: "" },
  rate:         { label: "Rată livrare",         polarity: "more_is_better", unit: "%", thresholds: [80, 92] },
  refusal_rate: { label: "Rată refuz",           polarity: "less_is_better", unit: "%", thresholds: [5, 12] },
  avg_transit_days: { label: "Timp tranzit",     polarity: "less_is_better", unit: "zile", thresholds: [2, 4] },
};

/**
 * Returns a hex color (red → orange → green) for a value, given the metric
 * thresholds. Supports both polarities.
 *
 * For non-threshold continuous metrics we use a 5-bucket quantile gradient
 * so that the color spread is meaningful even on heavily skewed distributions.
 */
function pickColor(value, metric, valueMin, valueMax, quantiles) {
  const cfg = METRIC_CONFIG[metric] || METRIC_CONFIG.orders;
  // Domain thresholds when available
  if (cfg.thresholds && Number.isFinite(value)) {
    const [low, high] = cfg.thresholds;
    if (cfg.polarity === "more_is_better") {
      if (value >= high) return "#10b981";  // emerald
      if (value >= low)  return "#f59e0b";  // amber
      return "#ef4444";                     // red
    }
    if (value <= low)  return "#10b981";
    if (value <= high) return "#f59e0b";
    return "#ef4444";
  }
  if (!Number.isFinite(value)) return "#e4e4e7";
  // Quantile-based bucketing (5 buckets) — vivid Tailwind 400/500 shades
  const PALETTE_MORE = ["#f87171", "#fb923c", "#fbbf24", "#4ade80", "#16a34a"]; // red→amber→green
  const PALETTE_LESS = ["#16a34a", "#4ade80", "#fbbf24", "#fb923c", "#f87171"]; // inverse
  const palette = cfg.polarity === "more_is_better" ? PALETTE_MORE : PALETTE_LESS;
  if (quantiles && quantiles.length === 4) {
    if (value <= quantiles[0]) return palette[0];
    if (value <= quantiles[1]) return palette[1];
    if (value <= quantiles[2]) return palette[2];
    if (value <= quantiles[3]) return palette[3];
    return palette[4];
  }
  // Fallback: simple linear interpolation if quantiles missing
  if (valueMax === valueMin) return palette[2];
  const t = (value - valueMin) / (valueMax - valueMin);
  const norm = cfg.polarity === "more_is_better" ? t : 1 - t;
  const bucket = Math.min(4, Math.floor(norm * 5));
  return palette[bucket];
}

const lerp = (a, b, t) => Math.round(a + (b - a) * Math.max(0, Math.min(1, t)));
const rgbHex = (r, g, b) =>
  "#" + [r, g, b].map((n) => n.toString(16).padStart(2, "0")).join("");

function computeQuantiles(values) {
  if (!values.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const q = (p) => sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * p))];
  return [q(0.2), q(0.4), q(0.6), q(0.8)];
}

/**
 * One county polygon — memoized to avoid rerender on unrelated state.
 */
const CountyPath = function CountyPath({
  county, fill, isSelected, isHovered, onEnter, onLeave, onClick,
}) {
  return (
    <path
      d={county.d}
      data-testid={`county-${county.code}`}
      data-county={county.code}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      onClick={onClick}
      fill={fill}
      stroke={isSelected ? "#0f172a" : "#ffffff"}
      strokeWidth={isSelected ? 2.2 : isHovered ? 1.6 : 0.9}
      style={{
        cursor: "pointer",
        filter: isSelected
          ? "drop-shadow(0 4px 12px rgba(15,23,42,0.35))"
          : isHovered
          ? "brightness(1.08)"
          : "none",
        transition: "filter 220ms ease, stroke-width 160ms ease",
      }}
    />
  );
};

/**
 * Main component. Props:
 *   data:      Array<{ county: string, [metric]: number }>
 *   metric:    string — which key to render
 *   title:     string — header label
 *   onSelect:  (countyCode | null) => void — selection callback
 *   selected:  countyCode | null — controlled selection
 */
export default function RomaniaCountyMap({
  data = [],
  metric = "orders",
  title,
  onSelect,
  selected: controlledSelected,
}) {
  const [hovered, setHovered] = useState(null);   // countyCode
  const [tooltip, setTooltip] = useState(null);   // {x, y, code, row}
  const [internalSelected, setInternalSelected] = useState(null);
  const containerRef = useRef(null);

  const selected = controlledSelected !== undefined ? controlledSelected : internalSelected;

  // Build lookup table code → metrics row
  const rowsByCode = useMemo(() => {
    const out = {};
    for (const row of data) {
      const code = normalizeCountyName(row.county);
      if (code) out[code] = row;
    }
    return out;
  }, [data]);

  // Determine value range and quantile cutoffs for color buckets
  const { vMin, vMax, quantiles } = useMemo(() => {
    const values = ROMANIA_COUNTIES
      .map((c) => rowsByCode[c.code]?.[metric])
      .filter((v) => Number.isFinite(v));
    if (!values.length) return { vMin: 0, vMax: 1, quantiles: null };
    return {
      vMin: Math.min(...values),
      vMax: Math.max(...values),
      quantiles: computeQuantiles(values),
    };
  }, [rowsByCode, metric]);

  const fillFor = useCallback(
    (code) => {
      const row = rowsByCode[code];
      if (!row) return "#f4f4f5"; // zinc-100 — no data
      const v = Number(row[metric]);
      if (!Number.isFinite(v)) return "#f4f4f5";
      return pickColor(v, metric, vMin, vMax, quantiles);
    },
    [rowsByCode, metric, vMin, vMax, quantiles]
  );

  const handleEnter = useCallback((code) => (e) => {
    setHovered(code);
    const rect = containerRef.current?.getBoundingClientRect();
    const x = e.clientX - (rect?.left || 0);
    const y = e.clientY - (rect?.top || 0);
    setTooltip({ x, y, code });
  }, []);

  const handleLeave = useCallback(() => {
    setHovered(null);
    setTooltip(null);
  }, []);

  const handleClick = useCallback((code) => () => {
    const next = code === selected ? null : code;
    if (controlledSelected === undefined) setInternalSelected(next);
    onSelect?.(next);
  }, [selected, controlledSelected, onSelect]);

  const cfg = METRIC_CONFIG[metric] || METRIC_CONFIG.orders;

  return (
    <div className="relative" ref={containerRef} data-testid="romania-county-map">
      {/* Legend */}
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {title || cfg.label}
        </div>
        <div className="flex items-center gap-2 text-[11px]">
          {cfg.thresholds ? (
            <>
              <LegendChip color="#ef4444" label={`Slab ${cfg.polarity === "more_is_better" ? `<${cfg.thresholds[0]}` : `>${cfg.thresholds[1]}`}${cfg.unit}`} />
              <LegendChip color="#f59e0b" label={`Mediu`} />
              <LegendChip color="#10b981" label={`Bun ${cfg.polarity === "more_is_better" ? `≥${cfg.thresholds[1]}` : `≤${cfg.thresholds[0]}`}${cfg.unit}`} />
            </>
          ) : (
            <>
              <LegendChip color="#ef4444" label="Mic" />
              <LegendChip color="#f59e0b" label="Mediu" />
              <LegendChip color="#10b981" label="Mare" />
            </>
          )}
          <LegendChip color="#f4f4f5" label="Fără date" border />
        </div>
      </div>

      {/* SVG canvas */}
      <motion.div
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.45, ease: "easeOut" }}
        className="relative w-full overflow-hidden rounded-lg bg-gradient-to-br from-slate-50 to-zinc-100 border border-border"
      >
        <svg
          viewBox="0 0 1300 1000"
          preserveAspectRatio="xMidYMid meet"
          className="block w-full h-auto"
          style={{ aspectRatio: "1300 / 1000" }}
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <filter id="county-glow" x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur stdDeviation="2.5" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {ROMANIA_COUNTIES.map((c) => (
            <CountyPath
              key={c.code}
              county={c}
              fill={fillFor(c.code)}
              isSelected={selected === c.code}
              isHovered={hovered === c.code}
              onEnter={handleEnter(c.code)}
              onLeave={handleLeave}
              onClick={handleClick(c.code)}
            />
          ))}

          {/* County code labels (subtle) */}
          {ROMANIA_COUNTIES.map((c) => (
            <text
              key={`lbl-${c.code}`}
              x={c.cx}
              y={c.cy}
              textAnchor="middle"
              dominantBaseline="central"
              pointerEvents="none"
              style={{
                fontSize: 18,
                fontWeight: 700,
                fill: "rgba(15,23,42,0.55)",
                fontFamily: "ui-sans-serif, system-ui, sans-serif",
                letterSpacing: 0.5,
                userSelect: "none",
              }}
            >
              {c.code}
            </text>
          ))}
        </svg>

        {/* Tooltip */}
        {tooltip && (
          <CountyTooltip
            x={tooltip.x}
            y={tooltip.y}
            code={tooltip.code}
            row={rowsByCode[tooltip.code]}
            metric={metric}
          />
        )}
      </motion.div>
    </div>
  );
}

function LegendChip({ color, label, border }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-muted-foreground">
      <span
        className="inline-block h-2.5 w-2.5 rounded-sm"
        style={{ background: color, border: border ? "1px solid #d4d4d8" : "none" }}
      />
      {label}
    </span>
  );
}

function CountyTooltip({ x, y, code, row, metric }) {
  const name = COUNTY_NAMES_BY_CODE[code] || code;
  // Clamp tooltip inside container (best-effort with simple offsets)
  const left = Math.min(x + 14, 1300);
  const top = Math.max(y - 90, 0);
  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.12 }}
      className="absolute pointer-events-none z-10 bg-white shadow-lg border border-border rounded-lg px-3.5 py-2.5 min-w-[200px]"
      style={{ left, top }}
    >
      <div className="text-sm font-bold tracking-tight text-foreground">
        {name} <span className="text-[10px] font-mono text-muted-foreground ml-1">{code}</span>
      </div>
      {row ? (
        <div className="mt-1.5 grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11px]">
          <Stat label="Colete" value={row.orders ?? "—"} />
          <Stat label="Livrate" value={row.delivered ?? "—"} />
          <Stat label="Refuzate" value={row.refused ?? "—"} />
          <Stat label="Retur" value={row.returned ?? "—"} />
          <Stat
            label="Rată livrare"
            value={row.rate != null ? `${Number(row.rate).toFixed(1)}%` : "—"}
            highlight={metric === "rate"}
          />
          <Stat
            label="Timp tranzit"
            value={row.avg_transit_days != null ? `${Number(row.avg_transit_days).toFixed(1)} z` : "—"}
            highlight={metric === "avg_transit_days"}
          />
          {row.refusal_rate != null && (
            <Stat label="Rată refuz" value={`${Number(row.refusal_rate).toFixed(1)}%`} highlight={metric === "refusal_rate"} />
          )}
          {row.best_courier && (
            <Stat label="Curier preferat" value={row.best_courier} />
          )}
        </div>
      ) : (
        <div className="text-[11px] text-muted-foreground mt-0.5">Fără livrări în perioadă</div>
      )}
    </motion.div>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <>
      <span className="text-muted-foreground">{label}</span>
      <span className={`text-right font-semibold ${highlight ? "text-primary" : "text-foreground"}`}>{value}</span>
    </>
  );
}
